import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { asciiScanField } from '../../components/asciiScanField';
import { CameraScanField } from '../../components/CameraScanField';

interface AddTrackingModalProps {
  /** What the header names — the request the modal was opened from, or the count of a bulk selection. */
  title: string;
  busy: boolean;
  refusal: string | null;
  onClose: () => void;
  onSave: (numbers: string[]) => void;
}

/** A dispatch email arrives as one block; newline, tab, comma and semicolon all separate numbers there. */
const SEPARATORS = /[\n\r\t,;]+/;

/**
 * [L-M1] Add Tracking No. One request can hold several numbers (split shipments), so the body is a
 * growable list rather than one field, and the keyboard drives it: Enter appends a row and never
 * saves, because an accidental Enter must not commit a half-transcribed set.
 */
export function AddTrackingModal({ title, busy, refusal, onClose, onSave }: AddTrackingModalProps) {
  const [rows, setRows] = useState<string[]>(['']);
  /** Set while a close was asked for over typed numbers; the numbers go only on an explicit Discard. */
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const focusIndex = useRef<number | null>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const keepEditingButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (focusIndex.current !== null) {
      inputs.current[focusIndex.current]?.focus();
      focusIndex.current = null;
    }
  });

  // The safe answer holds the focus, so a second reflexive Enter keeps the block rather than dropping it.
  useEffect(() => {
    if (confirmingDiscard) {
      keepEditingButton.current?.focus();
    }
  }, [confirmingDiscard]);

  const appendAfter = (index: number) => {
    setRows((previous) => {
      const next = [...previous];
      next.splice(index + 1, 0, '');
      return next;
    });
    focusIndex.current = index + 1;
  };

  const setRow = (index: number, value: string) =>
    setRows((previous) => previous.map((row, at) => (at === index ? value : row)));

  /** The last row is cleared rather than removed, so the list never reaches zero rows [E-37]. */
  const removeRow = (index: number) =>
    setRows((previous) => (previous.length === 1 ? [''] : previous.filter((_, at) => at !== index)));

  const paste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    const parts = event.clipboardData
      .getData('text')
      .split(SEPARATORS)
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
    if (parts.length < 2) {
      return;
    }
    // Splitting on paste lets the operator see and correct the result before saving [E-72].
    event.preventDefault();
    setRows((previous) => {
      const next = [...previous];
      next.splice(index, 1, ...parts);
      return next;
    });
  };

  const keyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    if ((rows[index] ?? '').trim() === '') {
      return;
    }
    appendAfter(index);
  };

  const saveable = [...new Set(rows.map((row) => row.trim()).filter((row) => row !== ''))];

  /**
   * Esc, the backdrop, ✕ and Cancel all land here. A transcribed block is minutes of work, so closing
   * over typed numbers asks first and only the explicit Discard lets them go. Nothing closes mid-save:
   * a refusal has to land in a modal that still holds the numbers it refused.
   */
  const requestClose = () => {
    if (busy) {
      return;
    }
    if (saveable.length === 0) {
      onClose();
      return;
    }
    setConfirmingDiscard(true);
  };

  /** Esc dismisses the topmost layer: while the discard question is up, that is the question, not the modal. */
  const escape = (event: KeyboardEvent) => {
    if (confirmingDiscard) {
      event.preventDefault();
      setConfirmingDiscard(false);
    }
  };

  return (
    <Modal
      show
      onHide={requestClose}
      onEscapeKeyDown={escape}
      backdrop
      centered
      aria-label={`Add Tracking No — ${title}`}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Tracking No — {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-1">Enter the tracking number(s) once the goods have shipped.</p>
        <p className="text-muted small">
          One inbound request can hold multiple tracking numbers (for split shipments) — every registered number becomes
          a View Orders match/scan target. Saving activates matching immediately; the status stays as it is.
        </p>
        {refusal && <Alert variant="danger">{refusal}</Alert>}
        {rows.map((row, index) => (
          <div className="d-flex gap-2 mb-2" key={index}>
            <CameraScanField label="Scan tracking number with camera" className="flex-grow-1">
              <Form.Control
                ref={(element: HTMLInputElement | null) => {
                  inputs.current[index] = element;
                }}
                size="sm"
                {...asciiScanField(
                  (value) => setRow(index, value),
                  (event) => keyDown(index, event),
                )}
                value={row}
                placeholder={
                  index === 0
                    ? 'e.g. 10325661220417 — last-mile / Coupang tracking number'
                    : 'Additional tracking number'
                }
                onPaste={(event) => paste(index, event as React.ClipboardEvent<HTMLInputElement>)}
              />
            </CameraScanField>
            <Button
              variant="outline-secondary"
              size="sm"
              aria-label="Remove this tracking number"
              onClick={() => removeRow(index)}
            >
              ✕
            </Button>
          </div>
        ))}
        <Button variant="outline-dark" size="sm" onClick={() => appendAfter(rows.length - 1)}>
          ＋ Add tracking number
        </Button>
      </Modal.Body>
      <Modal.Footer>
        {confirmingDiscard && (
          <Alert
            variant="warning"
            className="w-100 mb-2 d-flex flex-wrap gap-2 align-items-center"
            role="alertdialog"
            aria-labelledby="add-tracking-discard-question"
          >
            <span id="add-tracking-discard-question" className="me-auto">
              {`Discard ${saveable.length} unsaved tracking number${saveable.length === 1 ? '' : 's'}?`}
            </span>
            <div className="d-flex gap-2 ms-auto">
              <Button
                ref={keepEditingButton}
                variant="outline-secondary"
                size="sm"
                onClick={() => setConfirmingDiscard(false)}
              >
                Keep editing
              </Button>
              <Button variant="danger" size="sm" disabled={busy} onClick={onClose}>
                Discard
              </Button>
            </div>
          </Alert>
        )}
        <Button variant="outline-secondary" disabled={busy} onClick={requestClose}>
          Cancel
        </Button>
        <Button disabled={busy || saveable.length === 0} onClick={() => onSave(saveable)}>
          Save tracking numbers
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
